
import React, { useState, useEffect, ReactNode, ErrorInfo, Component, Suspense } from 'react';
import { Routes, Route, Navigate, BrowserRouter, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Non-lazy: Core layout & auth (needed immediately)
import Layout from './components/Layout.tsx';
import CustomerLayout from './components/CustomerLayout.tsx';
import { LanguageProvider } from './contexts/LanguageContext.tsx';
import { CurrencyProvider } from './contexts/CurrencyContext.tsx';
import { RBACProvider, useRBAC } from './contexts/RBACContext.tsx';
import { CustomerAuthProvider, useCustomerAuth } from './contexts/CustomerAuthContext.tsx';
import { LicenseProvider } from './contexts/LicenseContext.tsx';
import { Loader2, AlertCircle, RefreshCw, Hammer, X } from 'lucide-react';
import { api } from './utils/apiClient';

// ===================================================================
// LAZY-LOADED PAGES
// ===================================================================
const DashboardHome = React.lazy(() => import('./pages/DashboardHome.tsx'));
const ToursManagement = React.lazy(() => import('./pages/ToursManagement.tsx'));
const DestinationsManagement = React.lazy(() => import('./pages/DestinationsManagement.tsx'));
const CountriesManagement = React.lazy(() => import('./pages/CountriesManagement.tsx'));
const ActivitiesManagement = React.lazy(() => import('./pages/ActivitiesManagement.tsx'));
const AccommodationsManagement = React.lazy(() => import('./pages/AccommodationsManagement.tsx'));
const AIChatDashboard = React.lazy(() => import('./pages/AIChatDashboard.tsx'));
const SiteSettings = React.lazy(() => import('./pages/SiteSettings.tsx'));
const AdminsManagement = React.lazy(() => import('./pages/AdminsManagement.tsx'));
const RolesManagement = React.lazy(() => import('./pages/RolesManagement.tsx'));
const SeoDashboard = React.lazy(() => import('./pages/SeoDashboard.tsx'));
const EntityManager = React.lazy(() => import('./pages/EntityManager.tsx'));
const LocalSeoDashboard = React.lazy(() => import('./pages/LocalSeoDashboard.tsx'));
const BlogPostsManagement = React.lazy(() => import('./pages/BlogPostsManagement.tsx'));
const ContentPagesManagement = React.lazy(() => import('./pages/ContentPagesManagement.tsx'));
const MediaLibrary = React.lazy(() => import('./pages/MediaLibrary.tsx'));
const CustomersManagement = React.lazy(() => import('./pages/CustomersManagement.tsx'));
const PaymentsManagement = React.lazy(() => import('./pages/PaymentsManagement.tsx'));
const CurrenciesManagement = React.lazy(() => import('./pages/CurrenciesManagement.tsx'));
const PaymentGatewaysManagement = React.lazy(() => import('./pages/PaymentGatewaysManagement.tsx'));
const ReviewsManagement = React.lazy(() => import('./pages/ReviewsManagement.tsx'));
const AiSettings = React.lazy(() => import('./pages/AiSettings.tsx'));
const TourDeparturesManagement = React.lazy(() => import('./pages/TourDeparturesManagement.tsx'));
const SeoMetadataManagement = React.lazy(() => import('./pages/SeoMetadataManagement.tsx'));
const SchemaMarkupManagement = React.lazy(() => import('./pages/SchemaMarkupManagement.tsx'));
const SitemapManagement = React.lazy(() => import('./pages/SitemapManagement.tsx'));
const SeoAudits = React.lazy(() => import('./pages/SeoAudits.tsx'));
const SearchConsole = React.lazy(() => import('./pages/SearchConsole.tsx'));
const AnalyticsDashboard = React.lazy(() => import('./pages/AnalyticsDashboard.tsx'));
const AdvancedAnalyticsDashboard = React.lazy(() => import('./pages/AdvancedAnalyticsDashboard.tsx'));
const BookingsManagement = React.lazy(() => import('./pages/BookingsManagement.tsx'));
const TourBookingsManagement = React.lazy(() => import('./pages/TourBookingsManagement.tsx'));
const AITrainingData = React.lazy(() => import('./pages/AITrainingData.tsx'));
const ChatSessionsManagement = React.lazy(() => import('./pages/ChatSessionsManagement.tsx'));
const DataScrapingManagement = React.lazy(() => import('./pages/DataScrapingManagement.tsx'));
const CookiePoliciesManagement = React.lazy(() => import('./pages/CookiePoliciesManagement.tsx'));
const SystemUpdatesManagement = React.lazy(() => import('./pages/SystemUpdatesManagement.tsx'));
const SystemMaintenance = React.lazy(() => import('./pages/SystemMaintenance.tsx'));
const ApiCredentialsManagement = React.lazy(() => import('./pages/ApiCredentialsManagement.tsx'));
const TourPricingsManagement = React.lazy(() => import('./pages/TourPricingsManagement.tsx'));
const LanguageSettings = React.lazy(() => import('./pages/LanguageSettings.tsx'));
const BlogCategoriesManagement = React.lazy(() => import('./pages/BlogCategoriesManagement.tsx'));
const TagsManagement = React.lazy(() => import('./pages/TagsManagement.tsx'));
const BlogContentClustersManagement = React.lazy(() => import('./pages/BlogContentClustersManagement.tsx'));
const BlogContentGapsManagement = React.lazy(() => import('./pages/BlogContentGapsManagement.tsx'));
const BlogInternalLinkSuggestions = React.lazy(() => import('./pages/BlogInternalLinkSuggestions.tsx'));
const BlogEntityMentions = React.lazy(() => import('./pages/BlogEntityMentions.tsx'));
const CompetitorAnalyses = React.lazy(() => import('./pages/CompetitorAnalyses.tsx'));
const TopicalAuthorityDashboard = React.lazy(() => import('./pages/TopicalAuthorityDashboard.tsx'));
const KeywordResearch = React.lazy(() => import('./pages/KeywordResearch.tsx'));
const ThemeManagement = React.lazy(() => import('./pages/ThemeManagement.tsx'));
const ThemeCustomizer = React.lazy(() => import('./pages/ThemeCustomizer.tsx'));
const MenusManagement = React.lazy(() => import('./pages/MenusManagement.tsx'));
const CustomerLogin = React.lazy(() => import('./pages/CustomerLogin.tsx'));
const CustomerPortal = React.lazy(() => import('./pages/CustomerPortal.tsx'));
const GoogleMyBusinessAccounts = React.lazy(() => import('./pages/GoogleMyBusinessAccounts.tsx'));
const EmailTemplates = React.lazy(() => import('./pages/EmailTemplates.tsx'));
const EmailSettings = React.lazy(() => import('./pages/EmailSettings.tsx'));
const NotificationLog = React.lazy(() => import('./pages/NotificationLog.tsx'));
const AuthorsManagement = React.lazy(() => import('./pages/AuthorsManagement.tsx'));
const NewsletterManagement = React.lazy(() => import('./pages/NewsletterManagement.tsx'));
// const PublicWebsite = React.lazy(() => import('./pages/PublicWebsite.tsx')); (Now eager-loaded for speed)
const AdminLogin = React.lazy(() => import('./pages/AdminLogin.tsx'));
const LicenseManagement = React.lazy(() => import('./pages/LicenseManagement.tsx'));
const LicenseActivation = React.lazy(() => import('./pages/LicenseActivation.tsx'));
const ContactOfficesManagement = React.lazy(() => import('./pages/ContactOfficesManagement.tsx'));
const TourCategoriesManagement = React.lazy(() => import('./pages/TourCategoriesManagement.tsx'));
const ActivityCategoriesManagement = React.lazy(() => import('./pages/ActivityCategoriesManagement.tsx'));
const StorageSettings = React.lazy(() => import('./pages/StorageSettings.tsx'));

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 2 * 60 * 1000,
            gcTime: 30 * 60 * 1000,
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
            retry: 2,
        },
    },
});

const PageLoadingFallback: React.FC = () => (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <div className="relative flex flex-col items-center">
            <div className="w-16 h-16 relative mb-8">
                <div className="absolute inset-0 border-4 border-indigo-100 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-t-indigo-600 rounded-full animate-spin"></div>
            </div>
            <div className="text-center">
                <h2 className="text-xs font-black text-slate-900 uppercase tracking-[0.4em] mb-2">Preparing Experience</h2>
                <p className="text-[10px] text-slate-400 font-medium">Setting up your secure environment...</p>
            </div>
        </div>
        <div className="fixed bottom-10 text-center">
            <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest">Esiringet Cloud CMS &bull; v2.6</p>
        </div>
    </div>
);

interface ErrorBoundaryProps {
    children?: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

class GlobalErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    public state: ErrorBoundaryState = { hasError: false, error: null };

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Global Error Caught:", error, errorInfo);
        
        const errorMessage = error.message.toLowerCase();
        const isDynamicImportError = 
            errorMessage.includes('dynamically imported module') ||
            errorMessage.includes('loading chunk') ||
            errorMessage.includes('failed to fetch') ||
            errorMessage.includes('load chunk') ||
            errorMessage.includes('preload css');

        if (isDynamicImportError) {
            const lastReload = sessionStorage.getItem('es_auto_reload_attempt');
            const now = Date.now();
            
            // Allow auto-reload once every 10 seconds to prevent infinite loops
            if (!lastReload || now - parseInt(lastReload) > 10000) {
                console.warn("Dynamic import failure detected. Forcing application reload with cache bypass...");
                sessionStorage.setItem('es_auto_reload_attempt', now.toString());
                
                // Add a cache-busting parameter to ensure we fetch the latest index.html from server
                const url = new URL(window.location.href);
                url.searchParams.set('v', now.toString());
                window.location.replace(url.toString());
            }
        }
    }

    render() {
        if (this.state.error) {
            const msg = this.state.error.message.toLowerCase();
            const isImportError = 
                msg.includes('dynamically imported module') ||
                msg.includes('loading chunk') ||
                msg.includes('failed to fetch') ||
                msg.includes('load chunk') ||
                msg.includes('preload css');
            return (
                <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
                    <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center border border-red-100">
                        <div className="bg-red-50 p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                            <AlertCircle className="h-8 w-8 text-red-600" />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 mb-2">
                            {isImportError ? "Update Required" : "Something went wrong"}
                        </h2>
                        <p className="text-sm text-gray-500 mb-6">
                            {isImportError
                                ? "A new version of the CMS is available. Please reload to continue."
                                : "The application encountered a critical error."}
                        </p>
                        <div className="p-3 bg-red-50 rounded text-left overflow-auto max-h-32 text-xs font-mono text-red-800 border border-red-200 mb-6">
                            {this.state.error?.message}
                        </div>
                        <button
                            onClick={() => {
                                // Clear session storage to allow more auto-reloads if needed
                                sessionStorage.removeItem('es_auto_reload_attempt');
                                // Force reload with cache-buster
                                const now = Date.now();
                                const url = new URL(window.location.href);
                                url.searchParams.set('v', now.toString());
                                window.location.assign(url.toString());
                            }}
                            className="w-full py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors"
                        >
                            {isImportError ? "Refresh Now" : "Reload Application"}
                        </button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}

const ProtectedCustomerRoute: React.FC = () => {
    const { isAuthenticated, isLoading } = useCustomerAuth();
    if (isLoading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin h-10 w-10 text-blue-600" /></div>;
    return isAuthenticated ? <Outlet /> : <Navigate to="/portal/login" replace />;
};

const ProtectedAdminRoute: React.FC = () => {
    const { currentUser, isLoading } = useRBAC();
    if (isLoading) return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-slate-100 p-6 text-center">
            <Loader2 className="animate-spin h-12 w-12 text-indigo-600 mb-6" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Initializing System...</h2>
        </div>
    );
    if (!currentUser) return <Navigate to="/admin/login" replace />;
    return <Outlet />;
};

const DatabaseGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return <>{children}</>;
};

const App: React.FC = () => {
    return (
        <GlobalErrorBoundary>
            <QueryClientProvider client={queryClient}>
                <BrowserRouter>
                    <LanguageProvider>
                        <CurrencyProvider>
                            <RBACProvider>
                                <CustomerAuthProvider>
                                    <LicenseProvider>
                                        <Suspense fallback={<PageLoadingFallback />}>
                                            <Routes>
                                                <Route path="/admin/login" element={<AdminLogin />} />
                                                <Route path="/admin/activate" element={<LicenseActivation />} />

                                                <Route element={<DatabaseGuard><Outlet /></DatabaseGuard>}>
                                                    <Route path="/admin" element={<ProtectedAdminRoute />}>
                                                        <Route element={<Layout />}>
                                                            <Route index element={<DashboardHome />} />
                                                            <Route path="tours" element={<ToursManagement />} />
                                                            <Route path="tour-departures" element={<TourDeparturesManagement />} />
                                                            <Route path="tour-pricings" element={<TourPricingsManagement />} />
                                                            <Route path="bookings" element={<BookingsManagement />} />
                                                            <Route path="tour-bookings" element={<TourBookingsManagement />} />
                                                            <Route path="destinations" element={<DestinationsManagement />} />
                                                            <Route path="countries" element={<CountriesManagement />} />
                                                            <Route path="activities" element={<ActivitiesManagement />} />
                                                            <Route path="accommodations" element={<AccommodationsManagement />} />
                                                            <Route path="ai-chat-dashboard" element={<AIChatDashboard />} />
                                                            <Route path="chat-sessions" element={<ChatSessionsManagement />} />
                                                            <Route path="ai-training-data" element={<AITrainingData />} />
                                                            <Route path="data-scraping" element={<DataScrapingManagement />} />
                                                            <Route path="ai-settings" element={<AiSettings />} />
                                                            <Route path="customers" element={<CustomersManagement />} />
                                                            <Route path="payments" element={<PaymentsManagement />} />
                                                            <Route path="reviews" element={<ReviewsManagement />} />
                                                            <Route path="blog-posts" element={<BlogPostsManagement />} />
                                                            <Route path="authors" element={<AuthorsManagement />} />
                                                            <Route path="newsletter" element={<NewsletterManagement />} />
                                                            <Route path="blog-categories" element={<BlogCategoriesManagement />} />
                                                            <Route path="tour-categories" element={<TourCategoriesManagement />} />
                                                            <Route path="activity-categories" element={<ActivityCategoriesManagement />} />
                                                            <Route path="tags" element={<TagsManagement />} />
                                                            <Route path="content-pages" element={<ContentPagesManagement />} />
                                                            <Route path="media-folders" element={<MediaLibrary />} />
                                                            <Route path="analytics-dashboard" element={<AnalyticsDashboard />} />
                                                            <Route path="advanced-analytics" element={<AdvancedAnalyticsDashboard />} />
                                                            <Route path="seo-dashboard" element={<SeoDashboard />} />
                                                            <Route path="keyword-research" element={<KeywordResearch />} />
                                                            <Route path="topical-authority-dashboard" element={<TopicalAuthorityDashboard />} />
                                                            <Route path="search-console" element={<SearchConsole />} />
                                                            <Route path="central-entities" element={<EntityManager />} />
                                                            <Route path="blog-content-clusters" element={<BlogContentClustersManagement />} />
                                                            <Route path="blog-content-gaps" element={<BlogContentGapsManagement />} />
                                                            <Route path="blog-internal-link-suggestions" element={<BlogInternalLinkSuggestions />} />
                                                            <Route path="blog-entity-mentions" element={<BlogEntityMentions />} />
                                                            <Route path="local-seo-dashboard" element={<LocalSeoDashboard />} />
                                                            <Route path="google-my-business-accounts" element={<GoogleMyBusinessAccounts />} />
                                                            <Route path="seo-metas" element={<SeoMetadataManagement />} />
                                                            <Route path="schema-markups" element={<SchemaMarkupManagement />} />
                                                            <Route path="sitemap-dashboard" element={<SitemapManagement />} />
                                                            <Route path="competitor-analyses" element={<CompetitorAnalyses />} />
                                                            <Route path="seo-audits" element={<SeoAudits />} />
                                                            <Route path="themes" element={<ThemeManagement />} />
                                                            <Route path="theme-customizer" element={<ThemeCustomizer />} />
                                                            <Route path="menus" element={<MenusManagement />} />
                                                            <Route path="site-settings" element={<SiteSettings />} />
                                                            <Route path="languages" element={<LanguageSettings />} />
                                                            <Route path="email-templates" element={<EmailTemplates />} />
                                                            <Route path="email-settings" element={<EmailSettings />} />
                                                            <Route path="notification-log" element={<NotificationLog />} />
                                                            <Route path="cookie-policies" element={<CookiePoliciesManagement />} />
                                                            <Route path="system-updates" element={<SystemUpdatesManagement />} />
                                                            <Route path="system-maintenance" element={<SystemMaintenance />} />
                                                            <Route path="api-credentials" element={<ApiCredentialsManagement />} />
                                                            <Route path="license-management" element={<LicenseManagement />} />
                                                            <Route path="admins" element={<AdminsManagement />} />
                                                            <Route path="roles" element={<RolesManagement />} />
                                                            <Route path="currencies" element={<CurrenciesManagement />} />
                                                            <Route path="payment-gateways" element={<PaymentGatewaysManagement />} />
                                                            <Route path="contact-offices" element={<ContactOfficesManagement />} />
                                                            <Route path="storage-settings" element={<StorageSettings />} />
                                                        </Route>
                                                    </Route>
                                                    {/* Customer Portal Routes */}
                                                    <Route path="/portal/login" element={<CustomerLogin />} />
                                                    <Route element={<ProtectedCustomerRoute />}>
                                                        <Route element={<CustomerLayout />}>
                                                            <Route path="/portal" element={<Navigate to="/portal/dashboard" replace />} />
                                                            <Route path="/portal/dashboard" element={<CustomerPortal />} />
                                                            {/* Add more portal pages here as needed */}
                                                        </Route>
                                                    </Route>

                                                    <Route path="/portal/*" element={<Navigate to="/portal/login" replace />} />
                                                    <Route path="/" element={<Navigate to="/admin" replace />} />
                                                    <Route path="*" element={<Navigate to="/admin" replace />} />
                                                </Route>
                                            </Routes>
                                        </Suspense>
                                    </LicenseProvider>
                                </CustomerAuthProvider>
                            </RBACProvider>
                        </CurrencyProvider>
                    </LanguageProvider>
                </BrowserRouter>
            </QueryClientProvider>
        </GlobalErrorBoundary>
    );
};

export default App;


